/**
 * @fileoverview Business Logic (BL) for Personal Quick Reaction ("Reacción Rápida Personal" - Tile ID 45).
 * Encapsulates form sanitization, security checks (SQL Injection prevention), dual telemetry,
 * badge computation, sensory mobile hardware integration (Web Audio API synthesizer, Screen Wake Lock,
 * Haptic Vibration), session persistence, and delegates data access to the Data Layer (DL).
 * 
 * @module utilities/BL/PersonalQuickReaction
 */

import * as DLPersonalQuickReaction from './../DL/PersonalQuickReaction';
import * as XLSX from 'xlsx';
import { ValidateSqlInjection } from './Security';
import { GetSessionData as GetAuthSessionData } from './Sessions';

// Claves únicas para almacenamiento local de sesión de participante y audio
export const STORAGE_SESSION_KEY = 'interaktia_pqr_fan_session';
export const STORAGE_MUTE_KEY = 'interaktia_pqr_fan_muted';
export const FAN_SESSION_STORAGE_KEY = 'personal_quick_reaction_fan_session';
export const AUDIO_MUTED_STORAGE_KEY = 'pqr_audio_muted';

/**
 * Official badge definitions based on human reaction time thresholds.
 * @constant {Object}
 */
export const BADGES = {
    DIVINE: '⚡ Nivel Divino / Ojo de Halcón',
    FELINE: '🐆 Reflejos Felinos',
    FAST: '🤠 Pistolero Veloz',
    HUMAN: '🐢 Reflejo Humano',
    TIMEOUT: '🛑 Tiempo Agotado'
};

/**
 * The 7 conversion funnel telemetry milestones.
 * @constant {Object}
 */
export const TELEMETRY_STEPS = {
    QR_SCAN: 'qr_scan',
    PAGE_VIEW: 'page_view',
    FORM_START: 'form_start',
    FORM_COMPLETE: 'form_complete',
    GAME_START: 'game_start',
    TRIGGER_FIRED: 'trigger_fired',
    GAME_COMPLETE: 'game_complete'
};

// =============================================================================
// INPUT VALIDATIONS & SECURITY SANITIZATION
// =============================================================================

/**
 * Sanea texto de entrada para nombres o campos libres, eliminando caracteres de control.
 * 
 * @param {string} text - Texto a sanear.
 * @param {number} [maxLength=200] - Longitud máxima permitida.
 * @returns {string} Texto saneado.
 */
export const sanitizeInputText = (text, maxLength = 200) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .replace(/[\r\n\t\0]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, maxLength);
};

/**
 * Sanea un identificador GUID de sala.
 * 
 * @param {string|null} roomId - Identificador GUID.
 * @returns {string} RoomId saneado.
 */
export const sanitizeRoomId = (roomId) => {
    if (!roomId || typeof roomId !== 'string') return '';
    return roomId.replace(/[^a-zA-Z0-9-]/g, '').trim().substring(0, 50);
};

/**
 * Valida el nombre del participante (mínimo 2 caracteres, máximo 200).
 * 
 * @param {string} name - Nombre completo o alias.
 * @returns {boolean} Verdadero si es válido.
 */
export const validateParticipantName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length >= 2 && trimmed.length <= 200;
};

export const ValidateParticipantName = (name) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
        return { valid: false, message: 'El nombre del participante es obligatorio.' };
    }
    const clean = name.trim();
    if (clean.length < 2) {
        return { valid: false, message: 'El nombre debe contener al menos 2 caracteres.' };
    }
    if (clean.length > 200) {
        return { valid: false, message: 'El nombre no puede exceder 200 caracteres.' };
    }
    return { valid: true };
};

/**
 * Validates an email address against standard RFC 5322 compliance.
 * 
 * @function ValidateEmail
 * @param {string} email - Email address to validate.
 * @returns {{valid: boolean, message?: string}} Validation result.
 */
export const ValidateEmail = (email) => {
    if (!email || typeof email !== 'string' || email.trim() === '') {
        return { valid: false, message: 'El correo electrónico es obligatorio.' };
    }

    const trimmedEmail = email.trim();
    const rfc5322Regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

    if (!rfc5322Regex.test(trimmedEmail)) {
        return { valid: false, message: 'El formato de correo electrónico no es válido.' };
    }

    if (trimmedEmail.length > 200) {
        return { valid: false, message: 'El correo electrónico no puede exceder 200 caracteres.' };
    }

    return { valid: true };
};

export const validateEmail = (email) => {
    return ValidateEmail(email).valid;
};

/**
 * Validates a mobile phone number requiring 7 to 15 numeric digits.
 * 
 * @function ValidatePhone
 * @param {string} phone - Phone number to validate.
 * @returns {{valid: boolean, message?: string}} Validation result.
 */
export const ValidatePhone = (phone) => {
    if (!phone || typeof phone !== 'string' || phone.trim() === '') {
        return { valid: false, message: 'El número de teléfono es obligatorio.' };
    }

    const digitsOnly = phone.trim().replace(/\D/g, '');
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        return { valid: false, message: 'El número de teléfono debe contener entre 7 y 15 dígitos numéricos.' };
    }

    return { valid: true };
};

export const validatePhone = (phone) => {
    return ValidatePhone(phone).valid;
};

/**
 * Valida que los Términos y Condiciones hayan sido aceptados explícitamente.
 */
export const validateTerms = (acceptedTerms) => {
    return Boolean(acceptedTerms === true);
};

/**
 * Valida que el Aviso de Privacidad haya sido aceptado explícitamente.
 */
export const validatePrivacy = (acceptedPrivacy) => {
    return Boolean(acceptedPrivacy === true);
};

/**
 * Valida de forma integral el formulario de registro de participante móvil.
 * 
 * @param {Object} formData - Objeto con participantName, email, phone, acceptedTerms, acceptedPrivacy.
 * @returns {{ isValid: boolean, errors: Object }} Resultado y mapa de errores por campo.
 */
export const validateRegistrationForm = (formData = {}) => {
    const errors = {};

    const name = formData.participantName || formData.name;
    if (!validateParticipantName(name)) {
        errors.participantName = 'Ingresa tu nombre (mínimo 2 caracteres).';
    }

    if (!validateEmail(formData.email)) {
        errors.email = 'Ingresa un correo electrónico válido (ej. usuario@correo.com).';
    }

    if (!validatePhone(formData.phone)) {
        errors.phone = 'Ingresa un teléfono válido (al menos 7 a 10 dígitos numéricos).';
    }

    const terms = formData.acceptedTerms ?? formData.termsAccepted;
    if (!validateTerms(terms)) {
        errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones para participar.';
    }

    const privacy = formData.acceptedPrivacy ?? formData.privacyAccepted;
    if (!validatePrivacy(privacy)) {
        errors.acceptedPrivacy = 'Debes aceptar la Política de Privacidad.';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

export const ValidateRegistrationForm = async (formData = {}) => {
    const res = validateRegistrationForm(formData);
    return {
        valid: res.isValid,
        errors: res.errors,
        message: res.isValid ? '' : Object.values(res.errors)[0]
    };
};

// =============================================================================
// WEB AUDIO API SINTETIZADOR (ZERO MP3 DEPENDENCIES)
// =============================================================================

/**
 * Crea e inicializa una instancia del motor de síntesis de audio nativo con Web Audio API.
 * Proporciona beeps de conteo (440 Hz / 880 Hz), tono oscilante de tensión, trigger penetrante (1200 Hz),
 * acordes arpegiados de victoria, sonido disonante de falso inicio y control Mute/Unmute persistente.
 * 
 * @returns {Object} Instancia del motor de audio sintético.
 */
export const createSynthesizedAudioEngine = () => {
    let audioContext = null;
    let tensionOscillator = null;
    let tensionLfo = null;
    let tensionGainNode = null;
    let tensionFilterNode = null;

    let isMutedInternal = false;

    if (typeof sessionStorage !== 'undefined') {
        isMutedInternal = sessionStorage.getItem(STORAGE_MUTE_KEY) === 'true';
    }

    const getContext = () => {
        if (typeof window === 'undefined' && typeof global === 'undefined') return null;
        if (!audioContext) {
            const AudioContextClass = (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null) ||
                (typeof global !== 'undefined' ? (global.AudioContext || global.webkitAudioContext) : null);
            if (AudioContextClass) {
                try {
                    audioContext = new AudioContextClass();
                } catch {
                    audioContext = null;
                }
            }
        }
        return audioContext;
    };

    const unlock = async () => {
        try {
            const ctx = getContext();
            if (!ctx) return false;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            return ctx.state === 'running';
        } catch {
            return false;
        }
    };

    const setMuted = (muted) => {
        isMutedInternal = Boolean(muted);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_MUTE_KEY, isMutedInternal ? 'true' : 'false');
        }
        if (isMutedInternal) {
            stopTension();
        }
    };

    const isMuted = () => isMutedInternal;

    const playCountBeep = (countNumber) => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const frequency = countNumber === 1 ? 880 : 440;
        const duration = countNumber === 1 ? 0.35 : 0.28;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, now);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.45, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + duration + 0.02);
    };

    const playTension = () => {
        if (isMutedInternal) return;
        stopTension();

        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;

        try {
            tensionOscillator = ctx.createOscillator();
            tensionOscillator.type = 'triangle';
            tensionOscillator.frequency.setValueAtTime(75, now);

            tensionLfo = ctx.createOscillator();
            tensionLfo.type = 'sine';
            tensionLfo.frequency.setValueAtTime(3.5, now);

            const lfoGain = ctx.createGain();
            lfoGain.gain.setValueAtTime(15, now);

            tensionLfo.connect(lfoGain);
            lfoGain.connect(tensionOscillator.frequency);

            tensionFilterNode = ctx.createBiquadFilter();
            tensionFilterNode.type = 'lowpass';
            tensionFilterNode.frequency.setValueAtTime(160, now);

            tensionGainNode = ctx.createGain();
            tensionGainNode.gain.setValueAtTime(0.001, now);
            tensionGainNode.gain.exponentialRampToValueAtTime(0.3, now + 0.5);

            tensionOscillator.connect(tensionFilterNode);
            tensionFilterNode.connect(tensionGainNode);
            tensionGainNode.connect(ctx.destination);

            tensionOscillator.start(now);
            tensionLfo.start(now);
        } catch {}
    };

    const stopTension = () => {
        try {
            if (tensionGainNode && audioContext) {
                const now = audioContext.currentTime;
                tensionGainNode.gain.cancelScheduledValues(now);
                tensionGainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
            }

            setTimeout(() => {
                if (tensionOscillator) {
                    try { tensionOscillator.stop(); tensionOscillator.disconnect(); } catch {}
                    tensionOscillator = null;
                }
                if (tensionLfo) {
                    try { tensionLfo.stop(); tensionLfo.disconnect(); } catch {}
                    tensionLfo = null;
                }
                if (tensionFilterNode) {
                    try { tensionFilterNode.disconnect(); } catch {}
                    tensionFilterNode = null;
                }
                if (tensionGainNode) {
                    try { tensionGainNode.disconnect(); } catch {}
                    tensionGainNode = null;
                }
            }, 90);
        } catch {
            tensionOscillator = null;
            tensionLfo = null;
            tensionGainNode = null;
            tensionFilterNode = null;
        }
    };

    const playTrigger = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;

        const toneOsc = ctx.createOscillator();
        const toneGain = ctx.createGain();

        toneOsc.type = 'sawtooth';
        toneOsc.frequency.setValueAtTime(1200, now);
        toneOsc.frequency.exponentialRampToValueAtTime(400, now + 0.18);

        toneGain.gain.setValueAtTime(0.7, now);
        toneGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

        toneOsc.connect(toneGain);
        toneGain.connect(ctx.destination);

        try {
            const bufferSize = Math.floor(ctx.sampleRate * 0.12);
            const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const outputData = noiseBuffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                outputData[i] = Math.random() * 2 - 1;
            }

            const noiseSource = ctx.createBufferSource();
            noiseSource.buffer = noiseBuffer;

            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'lowpass';
            noiseFilter.frequency.setValueAtTime(5000, now);
            noiseFilter.frequency.exponentialRampToValueAtTime(200, now + 0.1);

            const noiseGain = ctx.createGain();
            noiseGain.gain.setValueAtTime(0.8, now);
            noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

            noiseSource.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(ctx.destination);

            noiseSource.start(now);
            noiseSource.stop(now + 0.13);
        } catch {}

        toneOsc.start(now);
        toneOsc.stop(now + 0.23);
    };

    const playVictory = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const notes = [
            { freq: 523.25, timeOffset: 0.00, duration: 0.18 },
            { freq: 659.25, timeOffset: 0.12, duration: 0.18 },
            { freq: 783.99, timeOffset: 0.24, duration: 0.22 },
            { freq: 1046.50, timeOffset: 0.38, duration: 0.65 }
        ];

        notes.forEach((note) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(note.freq, now + note.timeOffset);

            const startTime = now + note.timeOffset;
            gain.gain.setValueAtTime(0.001, startTime);
            gain.gain.exponentialRampToValueAtTime(0.4, startTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + note.duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(startTime);
            osc.stop(startTime + note.duration + 0.02);
        });
    };

    const playFalseStart = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.linearRampToValueAtTime(80, now + 0.32);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.5, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.34);
    };

    const dispose = () => {
        stopTension();
        if (audioContext) {
            try {
                audioContext.close();
            } catch {}
            audioContext = null;
        }
    };

    return {
        unlock,
        playCountBeep,
        playTension,
        stopTension,
        playTrigger,
        playVictory,
        playFalseStart,
        setMuted,
        isMuted,
        dispose
    };
};

export const PersonalQuickReactionAudio = {
    playCountBeep: (n) => { const e = createSynthesizedAudioEngine(); e.playCountBeep(n); },
    playTension: () => { const e = createSynthesizedAudioEngine(); e.playTension(); },
    stopTension: () => {},
    playTriggerSound: () => { const e = createSynthesizedAudioEngine(); e.playTrigger(); },
    playVictoryFanfare: () => { const e = createSynthesizedAudioEngine(); e.playVictory(); },
    playErrorSound: () => { const e = createSynthesizedAudioEngine(); e.playFalseStart(); }
};

// =============================================================================
// SCREEN WAKE LOCK API HELPER
// =============================================================================

let screenWakeLockSentinel = null;
let wakeLockVisibilityHandler = null;

export const IsWakeLockSupported = () => {
    return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
};

export const requestScreenWakeLock = async () => {
    try {
        if (!IsWakeLockSupported()) return false;
        if (screenWakeLockSentinel && !screenWakeLockSentinel.released) return true;

        screenWakeLockSentinel = await navigator.wakeLock.request('screen');

        if (!wakeLockVisibilityHandler && typeof document !== 'undefined') {
            wakeLockVisibilityHandler = async () => {
                if (document.visibilityState === 'visible' && (!screenWakeLockSentinel || screenWakeLockSentinel.released)) {
                    try {
                        screenWakeLockSentinel = await navigator.wakeLock.request('screen');
                    } catch {}
                }
            };
            document.addEventListener('visibilitychange', wakeLockVisibilityHandler);
        }

        return true;
    } catch {
        return false;
    }
};

export const RequestWakeLock = requestScreenWakeLock;

export const releaseScreenWakeLock = async () => {
    try {
        if (wakeLockVisibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', wakeLockVisibilityHandler);
            wakeLockVisibilityHandler = null;
        }

        if (screenWakeLockSentinel) {
            await screenWakeLockSentinel.release();
            screenWakeLockSentinel = null;
            return true;
        }
        return false;
    } catch {
        screenWakeLockSentinel = null;
        return false;
    }
};

export const ReleaseWakeLock = releaseScreenWakeLock;

// =============================================================================
// FEEDBACK HÁPTICO (NAVIGATOR.VIBRATE)
// =============================================================================

export const triggerHapticFeedback = (patternOrType = 20) => {
    try {
        const nav = typeof window !== 'undefined' && window.navigator && typeof window.navigator.vibrate === 'function'
            ? window.navigator
            : (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function' ? navigator : null);

        if (!nav) return false;

        if (typeof patternOrType === 'string') {
            const normalized = patternOrType.toLowerCase().trim();
            switch (normalized) {
                case 'trigger':
                    return nav.vibrate(40);
                case 'tap':
                    return nav.vibrate(20);
                case 'fault':
                    return nav.vibrate([60, 40, 60, 40, 100]);
                case 'victory':
                    return nav.vibrate([40, 50, 40, 50, 120]);
                default:
                    return nav.vibrate(20);
            }
        }

        if (Array.isArray(patternOrType) || typeof patternOrType === 'number') {
            return nav.vibrate(patternOrType);
        }

        return false;
    } catch {
        return false;
    }
};

export const Vibrate = triggerHapticFeedback;
export const VibrateTrigger = () => triggerHapticFeedback('trigger');
export const VibrateTap = () => triggerHapticFeedback('tap');
export const VibrateError = () => triggerHapticFeedback('fault');
export const VibrateVictory = () => triggerHapticFeedback('victory');

// =============================================================================
// PRECARGA DE ASSETS MULTIMEDIA EN SEGUNDO PLANO
// =============================================================================

const preloadedImagesCache = new Map();

export const resolveAssetUrl = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return '';
    const trimmed = mediaPath.trim();
    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('blob:') ||
        trimmed.startsWith('data:')
    ) {
        return trimmed;
    }

    const rawApi = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = rawApi.replace(/\/api\/?$/i, '');
    const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${backendOrigin}${normalizedPath}`;
};

export const resolveMediaUrl = resolveAssetUrl;

export const preloadThemeAssets = async (theme) => {
    if (!theme || typeof theme !== 'object' || typeof window === 'undefined') {
        return false;
    }

    const rawUrls = [
        theme.backgroundImageUrl,
        theme.logoImageUrl,
        theme.spriteBackUrl || '/img/games/quickReaction/back.png', //'/assets/quick-reaction/cowboy-back.png',
        theme.spriteFrontUrl || '/img/games/quickReaction/front.png', //'/assets/quick-reaction/cowboy-front.png',
        theme.spriteShootUrl,
        theme.spriteVictoryUrl,
        theme.banner1536Url,
        theme.banner1920Url
    ].filter((url) => typeof url === 'string' && url.trim().length > 0);

    const urlsToPreload = rawUrls.map(resolveAssetUrl);

    const loadPromises = urlsToPreload.map((url) => {
        return new Promise((resolve) => {
            if (preloadedImagesCache.has(url)) {
                resolve(true);
                return;
            }

            const img = new Image();
            img.onload = () => {
                preloadedImagesCache.set(url, img);
                resolve(true);
            };
            img.onerror = () => {
                preloadedImagesCache.set(url, false);
                resolve(false);
            };
            img.src = url;
        });
    });

    try {
        await Promise.all(loadPromises);
        return true;
    } catch {
        return false;
    }
};

// =============================================================================
// DETERMINACIÓN DE INSIGNIAS OFICIALES
// =============================================================================

export const getReactionBadge = (reactionTimeMs, isEarlyFault = false, isBelowHumanThreshold = false, isTimeout = false) => {
    if (isEarlyFault || isBelowHumanThreshold) {
        return {
            name: '⚠️ Salida Falsa / Descalificado',
            title: 'Salida Falsa',
            tier: 'fault',
            color: '#ff416c',
            icon: 'bi-exclamation-triangle-fill',
            description: 'Presionaste antes de la señal o por debajo del umbral fisiológico humano.'
        };
    }

    if (isTimeout) {
        return {
            name: '🛑 A Paso de Tortuga / Tiempo Agotado',
            title: 'Tiempo Agotado',
            tier: 'timeout',
            color: '#9e9e9e',
            icon: 'bi-hourglass-bottom',
            description: 'Se agotó el tiempo límite para reaccionar al disparo.'
        };
    }

    const ms = Number(reactionTimeMs) || 0;

    if (ms < 200) {
        return {
            name: '⚡ Nivel Divino / Ojo de Halcón',
            title: 'Nivel Divino',
            tier: 'god',
            color: '#00ffcc',
            icon: 'bi-lightning-charge-fill',
            description: '¡Reflejos sobrehumanos! Velocidad legendaria e instantánea.'
        };
    }

    if (ms >= 200 && ms <= 280) {
        return {
            name: '🐆 Reflejos Felinos',
            title: 'Reflejos Felinos',
            tier: 'elite',
            color: '#ffb703',
            icon: 'bi-fire',
            description: '¡Velocidad de élite! Reacción digna de un atleta profesional.'
        };
    }

    if (ms >= 281 && ms <= 380) {
        return {
            name: '🤠 Pistolero Veloz',
            title: 'Pistolero Veloz',
            tier: 'fast',
            color: '#00d2ff',
            icon: 'bi-award-fill',
            description: '¡Excelente desenfunde! Más rápido que el promedio general.'
        };
    }

    if (ms >= 381 && ms <= 500) {
        return {
            name: '🐢 Reflejo Humano',
            title: 'Reflejo Humano',
            tier: 'normal',
            color: '#70e000',
            icon: 'bi-person-check-fill',
            description: 'Buen intento dentro del rango estándar de velocidad humana.'
        };
    }

    return {
        name: '🛑 A Paso de Tortuga / Tiempo Agotado',
        title: 'Paso de Tortuga',
        tier: 'slow',
        color: '#fb8500',
        icon: 'bi-speedometer',
        description: 'Un poco lento, ¡sigue practicando para mejorar tu tiempo!'
    };
};

export const ComputeBadge = (reactionTimeMs, isEarlyLoss = false, isTimeout = false) => {
    return getReactionBadge(reactionTimeMs, isEarlyLoss, false, isTimeout).name;
};

export const GetBadgeMetadata = (badgeName) => {
    const defaultMeta = {
        badge: badgeName || BADGES.HUMAN,
        icon: 'bi-award-fill',
        color: '#00d2ff',
        cssClass: 'badge-human',
        description: 'Reflejos estándar dentro de la media humana.'
    };

    if (!badgeName) return defaultMeta;

    if (badgeName.includes('Divino') || badgeName.includes('Halcón')) {
        return {
            badge: BADGES.DIVINE,
            icon: 'bi-lightning-charge-fill',
            color: '#ffea00',
            cssClass: 'badge-divine',
            description: '¡Velocidad sobrehumana! Reflejos extraordinarios instantáneos.'
        };
    }
    if (badgeName.includes('Felinos')) {
        return {
            badge: BADGES.FELINE,
            icon: 'bi-lightning-fill',
            color: '#00e676',
            cssClass: 'badge-feline',
            description: '¡Reflejos de élite! Reacción digna de un atleta profesional.'
        };
    }
    if (badgeName.includes('Pistolero')) {
        return {
            badge: BADGES.FAST,
            icon: 'bi-award-fill',
            color: '#00e5ff',
            cssClass: 'badge-fast',
            description: '¡Excelente tiempo! Desenfundaste más rápido que el promedio.'
        };
    }
    if (badgeName.includes('Agotado') || badgeName.includes('Tortuga')) {
        return {
            badge: BADGES.TIMEOUT,
            icon: 'bi-hourglass-bottom',
            color: '#9e9e9e',
            cssClass: 'badge-timeout',
            description: 'Tiempo límite de reacción agotado.'
        };
    }
    return defaultMeta;
};

// =============================================================================
// TELEMETRÍA DUAL (BACKEND + GTM)
// =============================================================================

export const trackDualTelemetry = async ({ roomId, sessionId, stepName, source = 'mobile_fan', additionalData = {} }) => {
    try {
        if (!roomId || !stepName) return;

        DLPersonalQuickReaction.TrackTelemetry({
            roomId: roomId,
            sessionId: sessionId || 'session_' + Math.random().toString(36).substring(2, 10),
            stepName: stepName,
            source: source
        }).catch(() => {});

        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'personal_quick_reaction_step',
                step: stepName,
                roomId: roomId,
                sessionId: sessionId,
                timestamp: new Date().toISOString(),
                ...additionalData
            });
        }
    } catch {}
};

export const EmitTelemetryEvent = (stepName, roomId, sessionId = null, source = 'fan_mobile', metadata = {}) => {
    return trackDualTelemetry({ roomId, sessionId, stepName, source, additionalData: metadata });
};

// =============================================================================
// GESTIÓN DE SESIÓN PERSISTENTE LOCAL
// =============================================================================

export const getParticipantSession = (roomId) => {
    try {
        if (typeof localStorage === 'undefined' || !roomId) return null;
        const raw = localStorage.getItem(`${STORAGE_SESSION_KEY}_${roomId}`) || sessionStorage.getItem(`${FAN_SESSION_STORAGE_KEY}_${roomId}`);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const saveParticipantSession = (sessionData) => {
    try {
        if (!sessionData?.roomId) return;
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(`${STORAGE_SESSION_KEY}_${sessionData.roomId}`, JSON.stringify(sessionData));
        }
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(`${FAN_SESSION_STORAGE_KEY}_${sessionData.roomId}`, JSON.stringify(sessionData));
        }
    } catch {}
};

export const clearParticipantSession = (roomId) => {
    try {
        if (!roomId) return;
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(`${STORAGE_SESSION_KEY}_${roomId}`);
        }
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(`${FAN_SESSION_STORAGE_KEY}_${roomId}`);
            sessionStorage.removeItem(FAN_SESSION_STORAGE_KEY);
        }
    } catch {}
};

export const SaveFanSessionData = saveParticipantSession;
export const GetFanSessionData = (roomId) => getParticipantSession(roomId || '00000000-0000-0000-0000-000000000000');
export const ClearSessionStorage = () => clearParticipantSession('00000000-0000-0000-0000-000000000000');
export const ClearFanSessionData = ClearSessionStorage;

// =============================================================================
// TEXT FORMATTING & PII PRIVACY MASKING
// =============================================================================

export const FormatReactionTime = (milliseconds) => {
    if (milliseconds === null || milliseconds === undefined || isNaN(Number(milliseconds))) {
        return '--';
    }
    const ms = Number(milliseconds);
    return ms > 0 ? `${ms} ms` : '0 ms';
};

export const MaskEmail = (email) => {
    if (!email || typeof email !== 'string') return '***';
    const parts = email.split('@');
    if (parts.length !== 2) return '***';
    const user = parts[0];
    const domain = parts[1];
    if (user.length <= 2) return `${user[0]}***@${domain}`;
    return `${user[0]}***${user[user.length - 1]}@${domain}`;
};

export const MaskPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return '***';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return '***';
    return `******${digits.slice(-4)}`;
};

export const MaskName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return 'Aficionado';
    const tokens = fullName.trim().split(/\s+/);
    if (tokens.length === 1) return tokens[0];
    const firstName = tokens[0];
    const lastNameFirstLetter = tokens[1] ? tokens[1][0] : '';
    return `${firstName} ${lastNameFirstLetter}***`;
};

// =============================================================================
// BUSINESS LOGIC FACADE DELEGATING TO DATA LAYER (DL)
// =============================================================================

export const GetAdminOperatorName = async () => {
    try {
        const sessionRes = await GetAuthSessionData(true);
        if (sessionRes && sessionRes.success && sessionRes.data) {
            const user = sessionRes.data;
            const op = user.name || user.fullName || user.fullname || user.email || user.username || user.user;
            if (op && typeof op === 'string' && op.trim().length > 0) {
                return op.trim();
            }
        }
    } catch (e) {
        console.warn('No se pudo obtener el nombre de operador de la sesión:', e);
    }
    return 'Operador ADSL';
};

export const GetConfig = async (roomId) => {
    return await DLPersonalQuickReaction.GetConfig(roomId);
};

export const RegisterParticipant = async (formData) => {
    try {
        const validation = await ValidateRegistrationForm(formData);
        if (!validation.valid) {
            return { success: false, message: validation.message, errors: validation.errors };
        }

        const cleanName = (formData.participantName || formData.name || '').trim();
        const cleanEmail = (formData.email || '').trim().toLowerCase();
        const cleanPhone = (formData.phone || '').trim().replace(/\D/g, '');
        const acceptedTerms = Boolean(formData.acceptedTerms ?? formData.termsAccepted);
        const acceptedPrivacy = Boolean(formData.acceptedPrivacy ?? formData.privacyAccepted);
        const source = formData.source || formData.trafficSource || 'direct';

        const payload = {
            roomId: formData.roomId || '00000000-0000-0000-0000-000000000000',
            participantName: cleanName,
            name: cleanName,
            email: cleanEmail,
            phone: cleanPhone,
            acceptedTerms: acceptedTerms,
            termsAccepted: acceptedTerms,
            acceptedPrivacy: acceptedPrivacy,
            privacyAccepted: acceptedPrivacy,
            source: source,
            trafficSource: source
        };

        const result = await DLPersonalQuickReaction.RegisterParticipant(payload);
        if (result.success && result.data) {
            saveParticipantSession({
                participantId: result.data.participantId || result.data.id,
                oneTimeToken: result.data.oneTimeToken || result.data.token,
                participantName: cleanName,
                name: cleanName,
                email: cleanEmail,
                phone: cleanPhone,
                roomId: payload.roomId
            });
        }
        return result;
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar el registro.' };
    }
};

export const SubmitPlay = async (payload) => {
    try {
        if (!payload || !payload.participantId || !payload.oneTimeToken) {
            return { success: false, message: 'Identificador y token de un solo uso requeridos.' };
        }

        const clientMs = Number(payload.clientReactionTimeMs !== undefined ? payload.clientReactionTimeMs : payload.reactionTimeMs) || 0;
        const penaltyMs = Number(payload.penaltyTimeMs) || 0;
        const isEarly = Boolean(payload.isEarlyLoss || payload.isEarlyFault);
        const isTimeout = Boolean(payload.isTimeout || payload.outcomeStatus === 'TIMEOUT_EXPIRED' || payload.outcomeStatus === 'TIMEOUT');
        const outcome = payload.outcomeStatus || (isEarly ? 'EARLY_LOSS' : isTimeout ? 'TIMEOUT' : 'WIN');

        const sanitizedPayload = {
            roomId: payload.roomId,
            participantId: payload.participantId,
            oneTimeToken: payload.oneTimeToken,
            clientReactionTimeMs: clientMs,
            reactionTimeMs: clientMs,
            penaltyTimeMs: penaltyMs,
            earlyAttemptsCount: Number(payload.earlyAttemptsCount) || 0,
            outcomeStatus: outcome,
            isEarlyLoss: isEarly,
            isEarlyFault: isEarly,
            isTimeout: isTimeout,
            clientTimestampUtc: payload.clientTimestampUtc || new Date().toISOString()
        };

        return await DLPersonalQuickReaction.SubmitPlay(sanitizedPayload);
    } catch (error) {
        return { success: false, message: error.message || 'Error al registrar la jugada.' };
    }
};

export const GetRanking = async (roomId, limit = 20) => {
    try {
        const clampedLimit = Math.max(1, Math.min(Number(limit) || 20, 100));
        return await DLPersonalQuickReaction.GetRanking(roomId, clampedLimit);
    } catch (error) {
        return { success: false, message: error.message || 'Error al obtener el ranking.' };
    }
};

export const GetWinner = async (roomId) => {
    return await DLPersonalQuickReaction.GetWinner(roomId);
};

export const GetAdminData = async (roomId) => {
    return await DLPersonalQuickReaction.GetAdminData(roomId);
};

export const SaveConfig = async (configData) => {
    return await DLPersonalQuickReaction.SaveConfig(configData);
};

export const SaveOverlayConfig = async (overlayData) => {
    return await DLPersonalQuickReaction.SaveOverlayConfig(overlayData);
};

export const GetThemes = async (roomId) => {
    return await DLPersonalQuickReaction.GetThemes(roomId);
};

export const SaveTheme = async (themeData) => {
    try {
        if (!themeData || typeof themeData !== 'object') {
            return { success: false, message: 'Los datos del tema son obligatorios.' };
        }

        if (!themeData.name || typeof themeData.name !== 'string' || themeData.name.trim() === '') {
            return { success: false, message: 'El nombre del tema es obligatorio.' };
        }

        const trimmedName = themeData.name.trim();
        const isThreat = await ValidateSqlInjection(trimmedName);
        if (isThreat) {
            return { success: false, message: 'Se detectó un patrón no permitido en el nombre del tema.' };
        }

        const sanitizedPayload = {
            ...themeData,
            name: trimmedName
        };

        return await DLPersonalQuickReaction.SaveTheme(sanitizedPayload);
    } catch (error) {
        return { success: false, message: error.message || 'Error al validar y guardar el tema.' };
    }
};

export const DeleteTheme = async (roomId, themeId) => {
    return await DLPersonalQuickReaction.DeleteTheme(roomId, themeId);
};

export const ResetUser = async (participantId, reason, operator = null) => {
    try {
        if (!participantId) {
            return { success: false, message: 'Identificador de participante requerido.' };
        }

        if (!reason || typeof reason !== 'string' || reason.trim() === '') {
            return { success: false, message: 'El motivo del reintento es obligatorio para auditoría.' };
        }

        const finalOperator = operator || await GetAdminOperatorName();
        const payload = {
            participantId,
            reason: reason.trim(),
            operator: finalOperator
        };

        return await DLPersonalQuickReaction.ResetUser(payload);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar reintento de usuario.' };
    }
};

export const FullReset = async (roomId, masterPassword, operator = null) => {
    try {
        if (!roomId) {
            return { success: false, message: 'Identificador de sala requerido.' };
        }

        if (!masterPassword || typeof masterPassword !== 'string') {
            return { success: false, message: 'La contraseña maestra es requerida para esta acción.' };
        }

        const finalOperator = operator || await GetAdminOperatorName();
        const payload = {
            roomId,
            masterPassword: masterPassword.trim(),
            operator: finalOperator
        };

        return await DLPersonalQuickReaction.FullReset(payload);
    } catch (error) {
        return { success: false, message: error.message || 'Error al ejecutar purga total de la sala.' };
    }
};

export const UploadAsset = async (fileOrFiles, roomId, assetType = 'general') => {
    try {
        if (!fileOrFiles) {
            return { success: false, message: 'No se ha seleccionado ningún archivo de imagen.' };
        }

        const formData = new FormData();
        if (roomId) {
            formData.append('roomIdForm', roomId);
        }
        if (assetType) {
            formData.append('assetType', assetType);
        }

        if (Array.isArray(fileOrFiles) || fileOrFiles instanceof FileList) {
            for (let i = 0; i < fileOrFiles.length; i++) {
                formData.append('files', fileOrFiles[i]);
            }
        } else {
            formData.append('file', fileOrFiles);
        }

        return await DLPersonalQuickReaction.UploadAsset(formData, roomId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al subir el archivo multimedia.' };
    }
};

export const ExportPersonalQuickReactionToExcel = (dataset = {}, roomName = 'Reacción Rápida Personal') => {
    try {
        const participants = Array.isArray(dataset.participants) ? dataset.participants : (Array.isArray(dataset) ? dataset : []);
        const funnelMetrics = dataset.funnelMetrics || {};
        const auditLogs = Array.isArray(dataset.auditLogs) ? dataset.auditLogs : [];

        if (participants.length === 0 && auditLogs.length === 0) {
            return false;
        }

        const formatUtc = (isoStr) => {
            if (!isoStr) return 'N/A';
            try {
                return new Date(isoStr).toLocaleString('es-MX', { timeZone: 'America/Mexico_City', hour12: false }) + ' hrs';
            } catch {
                return isoStr;
            }
        };

        const rankedList = participants
            .filter(p => !p.isArchived && p.isTokenConsumed && p.outcomeStatus === 'WIN' && (p.reactionTimeMs || 0) > 0)
            .sort((a, b) => {
                const totalA = (a.reactionTimeMs || 0) + (a.penaltyTimeMs || 0);
                const totalB = (b.reactionTimeMs || 0) + (b.penaltyTimeMs || 0);
                if (totalA !== totalB) return totalA - totalB;
                if ((a.earlyAttemptsCount || 0) !== (b.earlyAttemptsCount || 0)) return (a.earlyAttemptsCount || 0) - (b.earlyAttemptsCount || 0);
                return new Date(a.registeredAtUtc || 0) - new Date(b.registeredAtUtc || 0);
            });

        const sheet1Data = rankedList.length > 0 ? rankedList.map((p, idx) => ({
            '# Posición': idx + 1,
            'Nombre Aficionado': p.participantName || p.name || 'Anónimo',
            'Correo Electrónico': p.email || 'N/A',
            'Teléfono': p.phone || 'N/A',
            'Tiempo Reacción (ms)': p.reactionTimeMs || 0,
            'Penalización (ms)': p.penaltyTimeMs || 0,
            'Tiempo Total (ms)': (p.reactionTimeMs || 0) + (p.penaltyTimeMs || 0),
            'Intentos Anticipados': p.earlyAttemptsCount || 0,
            'Insignia Obtenida': p.badgeName || 'Sin insignia',
            '¿Ganador Récord?': (idx === 0 || p.isWinner) ? 'SÍ 🏆' : 'NO',
            'Fecha y Hora Jugada': formatUtc(p.playedAtUtc)
        })) : [{ 'Estado': 'Sin partidas registradas aún para el ranking' }];

        const sortedAllParticipants = [...participants].sort((a, b) => new Date(b.registeredAtUtc || 0) - new Date(a.registeredAtUtc || 0));
        const sheet2Data = sortedAllParticipants.length > 0 ? sortedAllParticipants.map(p => ({
            'ID Participante': p.id,
            'Nombre': p.participantName || p.name || 'Anónimo',
            'Correo Electrónico': p.email || 'N/A',
            'Teléfono': p.phone || 'N/A',
            'Términos Aceptados': p.acceptedTerms ? 'SÍ' : 'NO',
            'Aviso Privacidad Aceptado': p.acceptedPrivacy ? 'SÍ' : 'NO',
            '¿Jugó Partida?': p.isTokenConsumed ? 'SÍ' : 'NO (Pendiente)',
            'Tiempo Reacción (ms)': p.reactionTimeMs != null ? p.reactionTimeMs : 'N/A',
            'Penalización (ms)': p.penaltyTimeMs || 0,
            'Estado Jugada': p.outcomeStatus || (p.isTokenConsumed ? 'FINALIZADO' : 'PENDIENTE'),
            '¿Descalificado?': (p.isEarlyFault || p.outcomeStatus === 'DISQUALIFIED') ? 'SÍ' : 'NO',
            '¿Reintento Concedido?': p.resetByAdmin ? 'SÍ' : 'NO',
            'Tiempo Previo (ms)': p.previousReactionTimeMs != null ? p.previousReactionTimeMs : 'N/A',
            'Token OTT Consumido': p.isTokenConsumed ? 'SÍ' : 'NO',
            'Dirección IP': p.clientIpAddress || 'N/A',
            'Navegador / Dispositivo': p.userAgent || 'N/A',
            'Fecha Registro': formatUtc(p.registeredAtUtc),
            'Fecha Jugada': formatUtc(p.playedAtUtc)
        })) : [{ 'Estado': 'Sin participantes registrados' }];

        const qrScan = funnelMetrics.qrScanCount || 0;
        const pageView = funnelMetrics.pageViewCount || 0;
        const formStart = funnelMetrics.formStartCount || 0;
        const formComplete = funnelMetrics.formCompleteCount || 0;
        const gameStart = funnelMetrics.gameStartCount || 0;
        const triggerFired = funnelMetrics.triggerFiredCount || 0;
        const gameComplete = funnelMetrics.gameCompleteCount || 0;
        const baseCount = Math.max(qrScan, pageView, 1);

        const sheet3Data = [
            { 'Hito del Embudo': '1. Escaneos de Código QR (qr_scan)', 'Total Eventos': qrScan, 'Tasa de Conversión': qrScan > 0 ? '100.0%' : '0%', 'Descripción': 'Aficionados que escanearon el QR en Live Overlay o estadio' },
            { 'Hito del Embudo': '2. Vistas de Página Web (page_view)', 'Total Eventos': pageView, 'Tasa de Conversión': `${((pageView / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Cargas exitosas de la aplicación web en el móvil' },
            { 'Hito del Embudo': '3. Formularios Iniciados (form_start)', 'Total Eventos': formStart, 'Tasa de Conversión': `${((formStart / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Aficionados que comenzaron a escribir sus datos' },
            { 'Hito del Embudo': '4. Formularios Completados (form_complete)', 'Total Eventos': formComplete, 'Tasa de Conversión': `${((formComplete / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Aficionados registrados con consentimientos legales' },
            { 'Hito del Embudo': '5. Partidas Iniciadas (game_start)', 'Total Eventos': gameStart, 'Tasa de Conversión': `${((gameStart / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Partidas donde inició la cuenta regresiva 3-2-1' },
            { 'Hito del Embudo': '6. Señales Trigger Disparadas (trigger_fired)', 'Total Eventos': triggerFired, 'Tasa de Conversión': `${((triggerFired / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Partidas donde el personaje giró y dio la señal de disparar' },
            { 'Hito del Embudo': '7. Partidas Finalizadas (game_complete)', 'Total Eventos': gameComplete, 'Tasa de Conversión': `${((gameComplete / baseCount) * 100).toFixed(1)}%`, 'Descripción': 'Jugadas completadas con registro de tiempo o descalificación' },
            { 'Hito del Embudo': '--- MÉTRICAS GLOBALES ---', 'Total Eventos': '', 'Tasa de Conversión': '', 'Descripción': '' },
            { 'Hito del Embudo': 'Récord de la Sala (Mejor Tiempo)', 'Total Eventos': funnelMetrics.recordReactionTimeMs != null ? `${funnelMetrics.recordReactionTimeMs} ms` : 'N/A', 'Tasa de Conversión': '⚡ Mejor Récord', 'Descripción': 'Tiempo más veloz registrado' },
            { 'Hito del Embudo': 'Tiempo Promedio de Reacción', 'Total Eventos': funnelMetrics.averageReactionTimeMs != null ? `${funnelMetrics.averageReactionTimeMs} ms` : 'N/A', 'Tasa de Conversión': 'Promedio', 'Descripción': 'Promedio de milisegundos de participantes válidos' },
            { 'Hito del Embudo': 'Tasa de Éxito de Partidas', 'Total Eventos': `${funnelMetrics.successRatePercentage || 0}%`, 'Tasa de Conversión': 'Finalización', 'Descripción': 'Porcentaje de partidas iniciadas que llegaron al final' }
        ];

        const sheet4Data = auditLogs.length > 0 ? auditLogs.map(l => ({
            'ID Auditoría': l.id,
            'ID Participante': l.participantId,
            'Nombre Participante': l.participantName || 'N/A',
            'Acción Realizada': l.actionType || 'SOFT_RESET',
            'Motivo Registrado': l.reason || 'N/A',
            'Tiempo Previo (ms)': l.previousScore != null ? l.previousScore : 'N/A',
            'Operador / Administrador': l.performedBy || 'Administrador',
            'Fecha y Hora': formatUtc(l.createdAtUtc)
        })) : [{ 'Estado': 'Sin registros de reintentos ni purgas de auditoría' }];

        const workbook = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(sheet1Data);
        const ws2 = XLSX.utils.json_to_sheet(sheet2Data);
        const ws3 = XLSX.utils.json_to_sheet(sheet3Data);
        const ws4 = XLSX.utils.json_to_sheet(sheet4Data);

        XLSX.utils.book_append_sheet(workbook, ws1, '1. Ranking Oficial');
        XLSX.utils.book_append_sheet(workbook, ws2, '2. Padrón Participantes');
        XLSX.utils.book_append_sheet(workbook, ws3, '3. Telemetría y Embudo');
        XLSX.utils.book_append_sheet(workbook, ws4, '4. Auditoría Reintentos');

        const cleanName = roomName.replace(/[^a-zA-Z0-9_-]/g, '_');
        const dateStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Reporte_Reaccion_Rapida_Personal_${cleanName}_${dateStr}.xlsx`);

        return true;
    } catch (err) {
        console.error('Error al exportar Excel de Reacción Rápida Personal:', err);
        return false;
    }
};

// Aliases BL
export const GetPublicConfigBL = GetConfig;
export const RegisterParticipantBL = RegisterParticipant;
export const SubmitPlayBL = SubmitPlay;
export const GetRankingBL = GetRanking;
export const GetWinnerBL = GetWinner;
export const GetAdminDataBL = GetAdminData;
export const UpdateAdminConfigBL = SaveConfig;
export const UpdateAdminOverlayBL = SaveOverlayConfig;
export const UploadAssetBL = UploadAsset;
export const GetThemesBL = GetThemes;
export const SaveThemeBL = SaveTheme;
export const ResetParticipantBL = ResetUser;
export const FullResetBL = FullReset;

const BLPersonalQuickReaction = {
    ValidateEmail,
    ValidatePhone,
    ValidateParticipantName,
    ValidateRegistrationForm,
    ComputeBadge,
    GetBadgeMetadata,
    EmitTelemetryEvent,
    PersonalQuickReactionAudio,
    IsWakeLockSupported,
    RequestWakeLock,
    ReleaseWakeLock,
    Vibrate,
    VibrateTrigger,
    VibrateTap,
    VibrateError,
    VibrateVictory,
    SaveFanSessionData,
    GetFanSessionData,
    ClearSessionStorage,
    ClearFanSessionData,
    FormatReactionTime,
    MaskEmail,
    MaskPhone,
    MaskName,
    resolveMediaUrl,
    resolveAssetUrl,
    GetAdminOperatorName,
    GetConfig,
    GetPublicConfigBL,
    RegisterParticipant,
    RegisterParticipantBL,
    SubmitPlay,
    SubmitPlayBL,
    GetRanking,
    GetRankingBL,
    GetWinner,
    GetWinnerBL,
    GetAdminData,
    GetAdminDataBL,
    SaveConfig,
    UpdateAdminConfigBL,
    SaveOverlayConfig,
    UpdateAdminOverlayBL,
    GetThemes,
    GetThemesBL,
    SaveTheme,
    SaveThemeBL,
    DeleteTheme,
    ResetUser,
    ResetParticipantBL,
    FullReset,
    FullResetBL,
    UploadAsset,
    UploadAssetBL,
    ExportPersonalQuickReactionToExcel,
    createSynthesizedAudioEngine,
    requestScreenWakeLock,
    releaseScreenWakeLock,
    triggerHapticFeedback,
    preloadThemeAssets,
    getReactionBadge,
    trackDualTelemetry,
    getParticipantSession,
    saveParticipantSession,
    clearParticipantSession,
    validateRegistrationForm,
    sanitizeRoomId
};

export default BLPersonalQuickReaction;
